import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import FormField from './ui/FormField';
import TagChip from './TagChip';
import roleManager, { PERMISSIONS } from '../utils/roleManager';
import { apiClient } from '../utils/apiClient';
import './CustomerTags.css';

// The tags on one customer, with a picker for changing them. Saving sends
// the whole set in one PUT (the API works out the adds and removes), so
// ticking three boxes is one request rather than three.
export default function CustomerTags({ userId, onChange }) {
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [newTagName, setNewTagName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit = roleManager.hasPermission(PERMISSIONS.EDIT_CUSTOMERS);
  const canManageTags = roleManager.hasPermission(PERMISSIONS.MANAGE_TAGS);

  // Held in a ref so a parent passing an inline arrow for onChange doesn't
  // change load()'s identity every render and re-fetch forever.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const rows = await apiClient.get(`/api/tags/customer/${userId}`);
      setTags(rows);
      onChangeRef.current?.(rows);
    } catch (err) {
      console.error('Error loading customer tags:', err);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const openPicker = async () => {
    setError('');
    setNewTagName('');
    try {
      const rows = await apiClient.get('/api/tags');
      setAllTags(rows);
      setSelected(new Set(tags.map((tag) => tag.id)));
      setPicking(true);
    } catch (err) {
      setError(err.message || 'Could not load the tag list.');
    }
  };

  const toggle = (tagId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
      return next;
    });
  };

  const createTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    setSaving(true);
    setError('');
    try {
      const tag = await apiClient.post('/api/tags', { name });
      setAllTags((prev) => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
      setSelected((prev) => new Set(prev).add(tag.id));
      setNewTagName('');
    } catch (err) {
      setError(err.message || 'Could not create that tag.');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const rows = await apiClient.put(`/api/tags/customer/${userId}`, { tag_ids: [...selected] });
      setTags(rows);
      onChangeRef.current?.(rows);
      setPicking(false);
    } catch (err) {
      setError(err.message || 'Could not save those tags.');
    } finally {
      setSaving(false);
    }
  };

  const removeOne = async (tag) => {
    const remaining = tags.filter((t) => t.id !== tag.id).map((t) => t.id);
    try {
      const rows = await apiClient.put(`/api/tags/customer/${userId}`, { tag_ids: remaining });
      setTags(rows);
      onChangeRef.current?.(rows);
    } catch (err) {
      console.error('Error removing tag:', err);
    }
  };

  return (
    <div className="customer-tags">
      <div className="customer-tags-list">
        {tags.length === 0 && <span className="customer-tags-empty">No tags</span>}
        {tags.map((tag) => (
          <TagChip key={tag.id} tag={tag} onRemove={canEdit ? removeOne : undefined} />
        ))}
        {canEdit && (
          <button type="button" className="customer-tags-add" onClick={openPicker}>
            <i className="fas fa-plus"></i> Tag
          </button>
        )}
      </div>

      <Modal
        open={picking}
        onClose={() => setPicking(false)}
        title="Tags"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setPicking(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </>
        )}
      >
        {allTags.length === 0 ? (
          <p className="customer-tags-empty">No tags exist yet — create the first one below.</p>
        ) : (
          <div className="customer-tags-options">
            {allTags.map((tag) => (
              <label key={tag.id} className="customer-tags-option">
                <input type="checkbox" checked={selected.has(tag.id)} onChange={() => toggle(tag.id)} />
                <TagChip tag={tag} />
                {tag.description && <span className="customer-tags-desc">{tag.description}</span>}
              </label>
            ))}
          </div>
        )}

        {canManageTags && (
          <div className="customer-tags-new">
            <FormField id="new-tag" label="Create a new tag">
              <div className="customer-tags-new-row">
                <input
                  id="new-tag"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="e.g. Legacy hosting"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createTag(); } }}
                />
                <Button variant="secondary" onClick={createTag} disabled={saving || !newTagName.trim()}>Add</Button>
              </div>
            </FormField>
          </div>
        )}

        {error && <div className="customer-tags-error">{error}</div>}
      </Modal>
    </div>
  );
}
